import React, { useEffect, useState, useRef } from "react";
import * as Icon from "react-bootstrap-icons";
import axios from "axios";
import { API_URL } from "../../config/constant";
import Spinner from "react-bootstrap/Spinner";
import checkPDFPasswordProtected from "../../utilities/checkPDFPasswordProtected";

const maxBankStatements = 4; // Arbitrary limit to prevent excessive refs
const bankStatementRefs = Array.from({ length: maxBankStatements }, () =>
  React.createRef()
);

export default function SelfEmployeeDocument({
  activeCard,
  preFillData,
  userType,
  businessTypeSelect,
  iFileType,
  handleCongratulation,
  caseId,
  loading,
}) {
  const [files, setFiles] = useState({
    panCard: { file: [], name: "", type: "", error: false, errorMsg: "" },
    businessRegistration: {
      file: [],
      name: "",
      type: "",
      error: false,
      errorMsg: "",
    },
    directorKyc: { file: [], name: "", type: "", error: false, errorMsg: "" },
    formSixteen: { file: [], name: "", type: "", error: false, errorMsg: "" },
    formSixteenP: { file: [], name: "", type: "", error: false, errorMsg: "" },
    udyamCertificate: {
      file: [],
      name: "",
      type: "",
      error: false,
      errorMsg: "",
    },
    gstReturn12: { file: [], name: "", type: "", error: false, errorMsg: "" },
    partnershipDeed: {
      // now a single object
      file: [],
      name: "",
      type: "",
      error: false,
      errorMsg: "",
    },
    bankStatements: [
      {
        file: [],
        name: "",
        type: "",
        error: false,
        errorMsg: "",
      },
    ],
  });
  const [isDisableBtn, setIsDisableBtn] = useState(false);
  const [display, setDisplay] = useState("none");
  const [fileData, setFileData] = useState(false);

  const fileRefs = {
    panCard: useRef(),
    businessRegistration: useRef(),
    directorKyc: useRef(),
    formSixteen: useRef(),
    formSixteenP: useRef(),
    udyamCertificate: useRef(),
    gstReturn12: useRef(),
    partnershipDeed: useRef(), // now a single ref
    bankStatements: bankStatementRefs,
  };

  const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
  const allowedPdfTypes = ["application/pdf"];
  const MAX_FILE_SIZE_IMAGE = 2048; // 2MB
  const MAX_FILE_SIZE_PDF = 5120; // 5MB

  const addBankStatement = () => {
    if (files.bankStatements.length >= maxBankStatements) return;
    setFiles((prev) => ({
      ...prev,
      bankStatements: [
        ...prev.bankStatements,
        {
          file: [],
          name: "",
          type: "",
          error: false,
          errorMsg: "",
        },
      ],
    }));
  };

  const handleFileChange = async (key, index, e) => {
    const selectedFile = e.target.files;
    if (!selectedFile.length) return;

    const file = selectedFile[0];
    const isImage = allowedTypes.includes(file.type);
    const isPdf = file.type === "application/pdf";
    const sizeLimit = isImage ? MAX_FILE_SIZE_IMAGE : MAX_FILE_SIZE_PDF;
    const validTypes = key === "panCard" ? allowedTypes : allowedPdfTypes;

    let error = false,
      errorMsg = "";
    if (!validTypes.includes(file.type)) {
      error = true;
      errorMsg = `Only ${
        key === "panCard" ? "JPEG, PNG, GIF" : "PDF"
      } allowed.`;
    } else if (file.size / 1024 > sizeLimit) {
      error = true;
      errorMsg = `File size exceeds ${sizeLimit / 1024}MB.`;
    }

    // Check for PDF password protection for bankStatements
    let passwordProtected = false;
    if (key === "bankStatements" && isPdf) {
      passwordProtected = await checkPDFPasswordProtected(file);
    }

    setFiles((prev) => {
      if (key === "bankStatements") {
        const updatedFiles = [...prev[key]];
        updatedFiles[index] = {
          file: [file],
          name: file.name,
          type: isPdf ? "pdf" : isImage ? file.type.split("/")[1] : "",
          error,
          errorMsg,
          passwordProtected, // add this property
          password: passwordProtected
            ? updatedFiles[index]?.password || ""
            : "",
          passwordError: false,
        };
        return { ...prev, [key]: updatedFiles };
      }
      // partnershipDeed is now a single object
      return {
        ...prev,
        [key]: {
          file: [file],
          name: file.name,
          type: isPdf ? "pdf" : isImage ? file.type.split("/")[1] : "",
          error,
          errorMsg,
        },
      };
    });
  };

  const removeFile = (key, index) => {
    console.log("Removing file:", key, index);
    if (key === "bankStatements" && files.bankStatements.length === 1) return;
    setFiles((prev) => {
      if (key === "bankStatements") {
        const updatedFiles = prev[key].filter((_, i) => i !== index);
        return { ...prev, [key]: updatedFiles };
      }
      // partnershipDeed is now a single object
      return {
        ...prev,
        [key]: {
          file: [],
          name: "",
          type: "",
          error: false,
          errorMsg: "",
        },
      };
    });
    if (key === "bankStatements")
      fileRefs.bankStatements[index].current.value = null;
    else fileRefs[key].current.value = null;
  };

  const handleValidation = () => {
    let isValid = true;
    const requiredFields = [
      "panCard",
      "businessRegistration",
      "udyamCertificate",
      "gstReturn12",
      ...(businessTypeSelect === "2" ? ["directorKyc"] : []),
      ...(iFileType !== "none" ? ["formSixteen", "formSixteenP"] : []),
      ...(businessTypeSelect === "3" || businessTypeSelect === "4"
        ? ["partnershipDeed"]
        : []),
    ];

    requiredFields.forEach((key) => {
      if (key === "partnershipDeed") {
        if (!files[key].file.length || files[key].errorMsg) {
          setFiles((prev) => ({
            ...prev,
            [key]: { ...prev[key], error: true },
          }));
          isValid = false;
        }
      } else if (!files[key].file.length || files[key].errorMsg) {
        setFiles((prev) => ({ ...prev, [key]: { ...prev[key], error: true } }));
        isValid = false;
      }
    });

    // Validate bank statements
    const validBankStatements = files.bankStatements.some(
      ({ file, errorMsg }) => file.length && !errorMsg
    );
    if (!validBankStatements) {
      setFiles((prev) => ({
        ...prev,
        bankStatements: prev.bankStatements.map((stmt) => ({
          ...stmt,
          error: true,
        })),
      }));
      isValid = false;
    }
    // Require password if passwordProtected
    files.bankStatements.forEach((stmt, index) => {
      if (stmt.passwordProtected && !stmt.password) {
        setFiles((prev) => {
          const updated = [...prev.bankStatements];
          updated[index] = {
            ...updated[index],
            passwordError: true,
          };
          return { ...prev, bankStatements: updated };
        });
        isValid = false;
      }
    });

    return isValid;
  };

  const handleUploadDocument = async () => {
    if (fileData) {
      handleCongratulation({
        name: preFillData?.personal_detail?.name,
        case_number: preFillData?.user_detail?.case_number,
      });
      return;
    }

    if (!handleValidation()) return;

    setDisplay("flex");
    setIsDisableBtn(true);

    try {
      const {
        data: { token },
      } = await axios.post(`${API_URL}token/generate-token`, {
        user_id: localStorage.getItem("user_id"),
      });

      const formData = new FormData();
      formData.append("case_id", caseId);
      formData.append("token", token);
      Object.entries(files).forEach(([key, value]) => {
        if (key === "bankStatements") {
          value.forEach(({ file, password }, index) => {
            if (file.length) {
              formData.append("bank_statement", file[0]);
              // Always send password if passwordProtected
              formData.append(
                `bank_statement_${index}_password`,
                password || ""
              );
            }
          });
        } else if (value.file.length) {
          const field =
            key === "formSixteen" || key === "formSixteenP"
              ? "itr"
              : key === "gstReturn12"
              ? "gst_return_12"
              : key.replace(/([A-Z])/g, "_$1").toLowerCase();
          formData.append(field, value.file[0]);
        }
      });

      const { data: response } = await axios.post(
        `${API_URL}files/upload`,
        formData
      );
      if (response?.status === 200) handleCongratulation(response?.data);
    } catch (e) {
      console.error(e);
    } finally {
      setDisplay("none");
      setIsDisableBtn(false);
    }
  };

  useEffect(() => {
    if (Object.keys(preFillData).length) {
      setFileData(!!preFillData?.files.length);
      const fileMap = {
        pan_card: "panCard",
        business_registration: "businessRegistration",
        director_kyc: "directorKyc",
        udyam_certificate: "udyamCertificate",
        gst_return_12: "gstReturn12",
        partnership_deed: "partnershipDeed",
        bank_statement: "bankStatements",
        itr: "formSixteen",
      };
      const newFiles = { ...files };
      const itrFiles = [];
      const bankStatementFiles = [];
      let partnershipDeedFile = null;

      preFillData?.files.forEach(({ document_type, filename }) => {
        if (document_type === "itr") itrFiles.push(filename);
        else if (document_type === "bank_statement")
          bankStatementFiles.push(filename);
        else if (document_type === "partnership_deed")
          partnershipDeedFile = filename;
        else if (fileMap[document_type]) {
          newFiles[fileMap[document_type]] = {
            ...newFiles[fileMap[document_type]],
            name: filename,
          };
        }
      });

      if (itrFiles.length) {
        ["formSixteen", "formSixteenP"].forEach((key, i) => {
          if (itrFiles[i])
            newFiles[key] = { ...newFiles[key], name: itrFiles[i] };
        });
      }
      if (bankStatementFiles.length) {
        newFiles.bankStatements = bankStatementFiles.map((filename) => ({
          file: [],
          name: filename,
          type: "",
          error: false,
          errorMsg: "",
        }));
      }
      if (partnershipDeedFile) {
        newFiles.partnershipDeed = {
          file: [],
          name: partnershipDeedFile,
          type: "",
          error: false,
          errorMsg: "",
        };
      }
      setFiles(newFiles);
    }
  }, [preFillData]);

  useEffect(() => loading(display), [display]);

  const startAndEnd = (str) =>
    str.length > 35 ? `${str.slice(0, 20)}...${str.slice(-10)}` : str;

  const renderFileUpload = (key, label, isPdfOnly = false, index) => {
    // partnershipDeed is now a single object, so ignore index for it
    const { file, name, type, error, errorMsg } =
      key === "bankStatements" ? files[key][index] : files[key];
    return (
      <div className="uploadfilesArea" key={`${key}-${index || 0}`}>
        <div className="filnameIcons">
          <div className="fileIc">
            <span className="imgWithName">
              <span className="imgIcons">
                {type === "pdf" ? (
                  <Icon.FiletypePdf
                    color={error ? "#f62b2b" : "#1962CD"}
                    size={50}
                  />
                ) : type === "png" ? (
                  <Icon.FiletypePng
                    color={error ? "#f62b2b" : "#1962CD"}
                    size={50}
                  />
                ) : type === "jpg" || type === "jpeg" ? (
                  <Icon.FiletypeJpg
                    color={error ? "#f62b2b" : "#1962CD"}
                    size={50}
                  />
                ) : type === "gif" ? (
                  <Icon.FiletypeGif
                    color={error ? "#f62b2b" : "#1962CD"}
                    size={50}
                  />
                ) : (
                  <Icon.Files color={error ? "#f62b2b" : "#1962CD"} size={50} />
                )}
              </span>
              <span className="imageName">
                <span
                  title={name}
                  className={error ? "vrtext error" : "vrtext"}
                >
                  {file.length || name ? (
                    <>
                      {label}
                      <br />
                      {startAndEnd(name)}
                    </>
                  ) : (
                    label
                  )}
                </span>
                {!file.length && !name && (
                  <span className="vrtext message">
                    * File Format: {isPdfOnly ? "PDF" : "JPEG, PNG, GIF"}; Max
                    size: {isPdfOnly ? "5MB" : "2MB"}
                  </span>
                )}
                {errorMsg && <span className="vrtext message">{errorMsg}</span>}
              </span>
            </span>
          </div>
          {file.length ? (
            <span className="greenTick" onClick={() => removeFile(key, index)}>
              <i className="fa fa-check"></i>
            </span>
          ) : (
            ""
          )}
        </div>
        <div
          className="uploadbtnSize"
          style={{ display: "flex", alignItems: "center" }}
        >
          <input
            type="file"
            accept={
              isPdfOnly ? "application/pdf" : "image/png,image/jpeg,image/gif"
            }
            onChange={(e) => handleFileChange(key, index, e)}
            style={{ display: "none" }}
            ref={
              key === "bankStatements"
                ? fileRefs.bankStatements[index]
                : fileRefs[key]
            }
          />
          <div
            className="uplodBtns"
            onClick={() =>
              (key === "bankStatements"
                ? fileRefs.bankStatements[index]
                : fileRefs[key]
              ).current.click()
            }
          >
            Upload <i className="fa fa-cloud-upload"></i>
          </div>
          {key === "bankStatements" && index === 0 && (
            <div className="uploadbtnSize">
              <div className="uplodBtns add-more" onClick={addBankStatement}>
                <i className="fa fa-plus"></i>
              </div>
            </div>
          )}
          {key === "bankStatements" && index > 0 && (
            <div
              className="uplodBtns removeBtn"
              onClick={() => removeFile(key, index)}
            >
              <i className="fa fa-trash"></i>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      className="col-lg-6 col-md-12"
      style={{
        display:
          activeCard === "upload-document" && userType === "employed"
            ? "block"
            : "none",
      }}
    >
      <h4 className="top-heading">Upload Documents</h4>
      <div className="form-group label">
        <div className="uploadsections p-20">
          {renderFileUpload("panCard", "Upload Pan Card Image", false, 0)}
          {renderFileUpload(
            "businessRegistration",
            "Upload Business Registration Proof",
            true,
            0
          )}
          {renderFileUpload(
            "udyamCertificate",
            "Upload Udyam Certificate",
            true,
            0
          )}
          {renderFileUpload(
            "gstReturn12",
            "Upload GST Return (Last 12 Months)",
            true,
            0
          )}
          {(businessTypeSelect === "3" || businessTypeSelect === "4") &&
            renderFileUpload(
              "partnershipDeed",
              "Upload Partnership Deed",
              true,
              0
            )}
          {businessTypeSelect === "2" &&
            renderFileUpload("directorKyc", "Upload MOA/AOA", true, 0)}
          {files.bankStatements.map((bankStmt, index) => (
            <>
              {renderFileUpload(
                "bankStatements",
                `Upload Bank Statement ${index + 1}`,
                true,
                index
              )}
              {bankStmt.passwordProtected && (
                <div
                  className="form-group loan-in"
                  style={{ color: "#1962cd" }}
                >
                  <label>{`Bank Statement ${index + 1} Password`}</label>
                  <input
                    type="text"
                    className={
                      bankStmt.passwordError
                        ? "salary-input error"
                        : "salary-input"
                    }
                    placeholder={`Enter Bank Statement ${index + 1} Password`}
                    value={bankStmt.password || ""}
                    onChange={(e) => {
                      setFiles((prev) => {
                        const updated = [...prev.bankStatements];
                        updated[index] = {
                          ...updated[index],
                          password: e.target.value,
                          passwordError: false,
                        };
                        return { ...prev, bankStatements: updated };
                      });
                    }}
                    style={{ borderBottomColor: "#1962cd" }}
                  />
                </div>
              )}
            </>
          ))}
          {(businessTypeSelect === "1" ||
            businessTypeSelect === "2" ||
            businessTypeSelect === "3" ||
            businessTypeSelect === "4" ||
            businessTypeSelect === "5" ||
            businessTypeSelect === "6") &&
            iFileType !== "none" && (
              <>
                {renderFileUpload(
                  "formSixteen",
                  "Upload Current Year ITR",
                  true,
                  0
                )}
                {renderFileUpload(
                  "formSixteenP",
                  "Upload Previous Year ITR",
                  true,
                  0
                )}
              </>
            )}
          <div>
            <button
              className="btn btn-login"
              onClick={handleUploadDocument}
              disabled={isDisableBtn}
            >
              {isDisableBtn ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  Please Wait...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
